import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaTools, FaCheckCircle, FaArrowRight, FaPhone, FaNetworkWired, FaWifi, FaClipboardCheck } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const NationalRolloutsPage = () => {
    const services = [
        {
            icon: <FaNetworkWired size={24} />,
            title: 'Network Refresh & Upgrade',
            description: 'Modernize your network infrastructure across all locations with coordinated upgrades and minimal downtime.',
            gradient: 'from-orange-500 to-red-500',
            link: '/services/network-refresh'
        },
        {
            icon: <FaWifi size={24} />,
            title: 'WAP Deployment',
            description: 'Enterprise wireless access point deployment with site surveys, installation, and optimization.',
            gradient: 'from-blue-600 to-cyan-600',
            link: '/services/wap-deployment'
        },
        {
            icon: <FaTools size={24} />,
            title: 'Hardware Deployment',
            description: 'Large-scale hardware rollouts including kiosks, POS systems, digital signage, and more.',
            gradient: 'from-purple-600 to-indigo-600',
            link: '/services/hardware-deployment'
        },
        {
            icon: <FaClipboardCheck size={24} />,
            title: 'Site Surveys',
            description: 'Comprehensive pre-deployment site assessments to ensure successful project execution.',
            gradient: 'from-teal-500 to-green-500',
            link: '/services/site-surveys'
        }
    ];

    const capabilities = [
        {
            number: 'Nationwide',
            label: 'Coverage',
            description: 'Service Points'
        },
        {
            number: '100%',
            label: 'Commitment',
            description: 'Successful rollouts'
        },
        {
            number: '24/7',
            label: 'Support',
            description: 'Always available'
        },
        {
            number: '99%',
            label: 'On-Time',
            description: 'Project completion'
        }
    ];

    const process = [
        {
            step: '01',
            title: 'Discovery & Planning',
            description: 'We assess your requirements, timeline, and budget to create a detailed project plan.'
        },
        {
            step: '02',
            title: 'Site Assessment',
            description: 'Our team conducts thorough site surveys to identify requirements and potential challenges.'
        },
        {
            step: '03',
            title: 'Coordinated Deployment',
            description: 'Expert technicians execute the rollout according to schedule with minimal disruption.'
        },
        {
            step: '04',
            title: 'Testing & Handover',
            description: 'Comprehensive testing and documentation ensures everything meets specifications.'
        }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-orange-900 to-red-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-orange-400">National Rollouts</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">National</span> Rollout Services
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Deploy technology solutions across multiple locations with precision, speed, and consistency. Coast-to-coast coverage you can count on.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-orange-500/30 transition-all duration-300">
                                    Start Your Rollout
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop"
                                    alt="National Rollouts"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {capabilities.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500 mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-xl font-semibold text-gray-900">{stat.label}</div>
                                <div className="text-sm text-gray-500">{stat.description}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">Rollout Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Comprehensive Deployment Solutions</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From network upgrades to hardware installations, we handle all aspects of your multi-site deployment
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {services.map((service, index) => (
                            <Link
                                key={index}
                                to={service.link}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-orange-200"
                            >
                                <div className="flex items-start gap-6">
                                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                                        {service.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">{service.title}</h3>
                                        <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                                        <span className="inline-flex items-center gap-2 text-orange-600 font-semibold">
                                            Learn More <FaArrowRight size={12} />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-orange-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">Our Process</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How We Deliver Success</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            A proven methodology that ensures consistent, high-quality deployments every time
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {process.map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg relative">
                                <div className="text-6xl font-bold text-orange-100 absolute top-4 right-4">{step.step}</div>
                                <div className="relative z-10">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                                    <p className="text-gray-600">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Coverage Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=500&fit=crop"
                                alt="Nationwide Coverage"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">Coverage</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Coast-to-Coast Deployment</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                With service points across North America, we can deploy to any location with local expertise and national coordination.
                            </p>
                            <div className="space-y-4">
                                {['Single point of contact for all locations', 'Consistent quality across all sites', 'Flexible scheduling options', 'Real-time project tracking', 'Comprehensive documentation'].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-orange-500 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 to-red-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Scale Your Deployment?</h2>
                    <p className="text-xl text-orange-100 mb-8">
                        Contact us today to discuss your multi-site project and get a customized rollout plan.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Request Project Quote
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Speak to an Expert
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default NationalRolloutsPage;
